/**
 * WorkoutVariant — energetic kinetic-type ad. Athletic / sportswear vibe.
 */
import { motion } from "framer-motion";
import Wordmark from "./Wordmark";
import { COPY } from "./copy";

export default function WorkoutVariant({ portrait }) {
  const headlineSize = portrait ? "text-[160px]" : "text-[140px]";

  return (
    <div className="relative w-full h-full overflow-hidden" style={{ background: "#0F172A" }}>
      <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 30%, #5B21B6 60%, #BE185D 100%)" }} />
      <motion.div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "repeating-linear-gradient(135deg, rgba(255,255,255,0.15) 0px, rgba(255,255,255,0.15) 4px, transparent 4px, transparent 100px)",
        }}
        animate={{ backgroundPosition: ["0px 0px", "400px 400px"] }}
        transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        className="absolute top-0 left-0 h-3 bg-gradient-to-r from-pink-500 via-orange-400 to-yellow-300"
        initial={{ width: "0%" }} animate={{ width: "100%" }}
        transition={{ delay: 0.2, duration: 1.2, ease: "easeOut" }}
      />
      <motion.div
        className="absolute bottom-0 right-0 h-3 bg-gradient-to-l from-pink-500 via-orange-400 to-yellow-300"
        initial={{ width: "0%" }} animate={{ width: "100%" }}
        transition={{ delay: 0.4, duration: 1.2, ease: "easeOut" }}
      />

      <motion.div
        className="absolute top-16 right-16 px-6 py-3 rounded-full text-white font-black uppercase text-sm tracking-[0.3em]"
        style={{ background: "rgba(244,63,94,0.85)", boxShadow: "0 8px 30px rgba(244,63,94,0.4)" }}
        animate={{ scale: [1, 1.06, 1] }}
        transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
      >
        + Rep
      </motion.div>
      <motion.div
        className="absolute bottom-32 left-12 px-5 py-3 rounded-full text-white font-black uppercase text-sm tracking-[0.3em]"
        style={{ background: "rgba(34,197,94,0.85)", boxShadow: "0 8px 30px rgba(34,197,94,0.4)" }}
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
      >
        Log set ✓
      </motion.div>
      <motion.div
        className="absolute top-1/3 left-8 px-4 py-2 rounded-full text-white font-bold text-xs uppercase tracking-widest"
        style={{ background: "rgba(251,191,36,0.85)" }}
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
      >
        🔥 Streak · 12 days
      </motion.div>

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-12 text-white">
        <Wordmark
          size={portrait ? "xl" : "lg"}
          glpColor="#FFFFFF"
          floClass="bg-gradient-to-r from-yellow-300 via-orange-400 to-pink-500 bg-clip-text text-transparent"
          delay={0.2}
        />
        <motion.p
          initial={{ opacity: 0, x: -100 }} animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
          className="text-[14px] tracking-[0.4em] font-black mt-2 mb-6 uppercase text-yellow-300"
        >
          Train. Track. Transform.
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.9, duration: 0.7, ease: "easeOut" }}
          className={`${headlineSize} font-black leading-[0.88] tracking-tighter italic`}
          style={{ WebkitTextStroke: "1px rgba(255,255,255,0.2)" }}
        >
          {COPY.headline1}
          <br />
          <span className="bg-gradient-to-r from-yellow-300 via-orange-400 to-pink-500 bg-clip-text text-transparent">{COPY.headline2}</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, x: 100 }} animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.6, duration: 0.6 }}
          className="text-3xl mt-6 font-black uppercase tracking-wider text-yellow-300 italic"
        >
          {COPY.sub}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.3, duration: 0.7 }}
          className="mt-6 max-w-[880px]"
        >
          <p className="text-xl text-white font-bold mb-2">{COPY.body1}</p>
          <p className="text-lg text-pink-100/90 mb-2">{COPY.body2}</p>
          <p className="text-base text-yellow-100/90 font-medium">{COPY.body3}</p>
        </motion.div>
      </div>

      <motion.p
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ delay: 3.2, duration: 0.5 }}
        className="absolute bottom-10 left-0 right-0 text-center text-sm tracking-[0.4em] uppercase font-black text-yellow-300"
      >
        {COPY.footer} ·{" "}
        <span className="text-white border-b-2 border-yellow-300 pb-1">{COPY.url}</span>
      </motion.p>
    </div>
  );
}
